import React from 'react'
import AttributedHtmlText from '#src/components/AttributedHtmlText'
import { FormContextConsumer, FormContextType } from '#src/components/Path/FormContext'
import {
  Group,
  DefaultImageSelectable,
  FullWidthImageSelectable,
  OverlayImageSelectable,
  DefaultInputSelectable,
  SimpleRadioSelectable,
  SelectableProps
} from '#src/components/Path/Selectable'
import { Set } from 'immutable'
import { ListPathFlowQuestionType, ListPathFlowQuestionRenderStyle } from '#src/generated/graphql'
import { ListPathFlowQuestionProps } from '../'
import styles from '../../../../../components/ListPathFlowQuestion/SelectList/styles.css'

class SelectList extends React.PureComponent<
  ListPathFlowQuestionProps & { context: FormContextType }
> {
  isMultiSelect: boolean
  isSingleSelect: boolean
  isRadioSelect: boolean
  isFullWidthImage: boolean

  constructor(props: ListPathFlowQuestionProps & { context: FormContextType }) {
    super(props)

    this.isRadioSelect = props.question.renderStyle === ListPathFlowQuestionRenderStyle.SIMPLE_RADIO
    this.isSingleSelect = props.question.listType === ListPathFlowQuestionType.SINGLE_SELECT_LIST
    this.isMultiSelect = props.question.listType === ListPathFlowQuestionType.MULTI_SELECT_LIST
    this.isFullWidthImage =
      props.question.renderStyle === ListPathFlowQuestionRenderStyle.FULL_WIDTH_IMAGES
  }

  state = {
    pristine: true
  }

  public componentDidUpdate() {
    /**
     * If the form is valid, it's a single select question and
     * this isn't a radio select, submit the form. This will be called
     * on update of the form's validity.
     */
    if (
      this.props.context &&
      this.props.context.isFormValid &&
      this.isSingleSelect &&
      !this.state.pristine &&
      !this.isRadioSelect
    ) {
      this.setState(
        {
          pristine: true
        },
        this.props.context.onPageSubmitted
      )
    }
  }

  public render() {
    const {
      question,
      onFieldValueChanged,
      onFieldValidityChanged,
      hideIcon,
      className,
      animationDelay,
      style
    } = this.props
    const defaultValues = question.listUserInput.value ? Set(question.listUserInput.value) : Set()

    let containerColumnClass: string = ''
    let choiceColumnClass: string = ''
    switch (question.choices.length) {
      case 2:
        containerColumnClass = styles.containerTwo
        choiceColumnClass = styles.choiceTwo
        break

      case 3:
        containerColumnClass = styles.containerThree
        choiceColumnClass = styles.choiceThree
        break

      case 4:
      default:
        containerColumnClass = styles.containerFour
        choiceColumnClass = styles.choiceFour
        break
    }

    return (
      <div className={containerColumnClass}>
        <Group
          name={question.internalName}
          onSelect={values => {
            onFieldValueChanged({ value: [...values] }, () => {
              this.setState({
                pristine: false
              })
            })
          }}
          multiple={this.isMultiSelect}
          defaultValues={defaultValues}
          onValid={() => onFieldValidityChanged(true)}
          onInvalid={() => onFieldValidityChanged(false)}
        >
          {question.choices.map((choice, index) => {
            const headline = choice.text ? <AttributedHtmlText text={choice.text} /> : ''
            const subText = choice.subText ? <AttributedHtmlText text={choice.subText} /> : ''

            const image = choice.primaryImage ? choice.primaryImage.imageUrl : undefined
            const icon = choice.iconImage ? choice.iconImage.imageUrl : undefined

            let SelectableComponent: React.ComponentClass<SelectableProps>
            if (image) {
              if (question.renderStyle === ListPathFlowQuestionRenderStyle.FULL_WIDTH_IMAGES) {
                SelectableComponent = FullWidthImageSelectable
              } else if (question.renderStyle === ListPathFlowQuestionRenderStyle.OVERLAY_IMAGE) {
                SelectableComponent = OverlayImageSelectable
              } else {
                SelectableComponent = DefaultImageSelectable
              }
            } else {
              if (question.renderStyle === ListPathFlowQuestionRenderStyle.SIMPLE_RADIO) {
                SelectableComponent = SimpleRadioSelectable
              } else {
                SelectableComponent = DefaultInputSelectable
              }
            }

            return (
              <div className={choiceColumnClass} key={choice.value}>
                <SelectableComponent
                  isSingleSelect={this.isSingleSelect}
                  value={choice.value}
                  icon={!hideIcon ? icon : undefined}
                  headline={headline}
                  body={subText}
                  image={image}
                  className={className}
                  style={{ ...style, animationDelay: `${animationDelay * (index! + 1)}s` }}
                />
              </div>
            )
          })}
        </Group>
      </div>
    )
  }
}

const SelectListWithContext: React.SFC<ListPathFlowQuestionProps> = ({ ...rest }) => (
  <FormContextConsumer>
    {formData => (formData ? <SelectList context={formData} {...rest} /> : null)}
  </FormContextConsumer>
)

export default SelectListWithContext
